import { BackArrow } from 'assets/icons';
import cx from 'classnames';
import Button from 'components/atoms/Button';
import BottomSheet from 'components/molecules/BottomSheet';
import SlotBox from 'components/organisms/SlotBox';
import { BUTTON_NAMES } from 'constant/button-names';
import { useTimeSlotSelector } from 'packages/shared/newOrder/hooks/useTimeSlotSelector';
import React, { useCallback, useMemo } from 'react';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import type { NewOrderModule } from '../../types';
import styles from './styles.module.scss';

const StyledBackButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.white};
  border-color: ${({ theme }) => theme.text.tertiary};
  color: ${({ theme }) => theme.colors.subtitleDark};
  width: 68px;
  border-width: 1px;
  margin-right: 10px;
  margin-top: 8px;
`;

const StyledDiv = styled.div`
  display: flex;
  position: sticky;
  background-color: ${({ theme }) => theme.colors.white};
  border-width: 0px;
  padding: 8px;
`;

const TimeSlotSelectorComponent: React.FC<NewOrderModule.ITimeSlotSelector> = ({
  show = false,
  onClose = () => {},
  onSubmit = () => {},
  action = 'PICKUP',
  timeslotsData,
  selectedDateTimeSlot,
  isAtDoor = true,
  isShowServiceTitle = false,
  serviceLine,
  isLoading,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { selectedSlots, dateRefs, slotRefs, handleDateSelected, handleSlotSelected, DateTimeSlots, SelectedSlot, capitalizeText } =
    useTimeSlotSelector({
      timeslotsData,
      selectedDateTimeSlot,
      show,
    });

  const SheetTitle = useMemo(
    () =>
      isShowServiceTitle
        ? t(`newOrderPage.deliveryBottomSheetHeading.${isAtDoor ? 'AT_DOOR' : 'IN_PERSON'}.${serviceLine}`)
        : `${action === 'DROPOFF' ? t('common.delivery') : t('common.pickUp')} ${isAtDoor ? t('common.atDoor') : t('common.inPerson')}`,
    [action, isAtDoor, isShowServiceTitle, serviceLine, t]
  );

  const onSubmitSheet = useCallback(() => {
    if (SelectedSlot) {
      const dateTimeSlot = {
        ...SelectedSlot,
        date: selectedSlots.dateSlotId,
      };
      onSubmit(dateTimeSlot);
    }
  }, [SelectedSlot]);

  return (
    <BottomSheet isNoWrapTitle={isShowServiceTitle} isCapitalizeHeaderText show={show} onSubmit={onSubmitSheet} onClose={onClose} title={SheetTitle}>
      {timeslotsData && timeslotsData.dates && (
        <Row>
          <Col xs={5} className={cx('d-flex flex-column flex-nowrap overflow-auto padding-start-6 padding-end-2', styles.timeslotsWrapper)}>
            {timeslotsData.dates.map((date) => (
              <SlotBox
                key={date.date}
                // eslint-disable-next-line no-return-assign
                boxRef={(ref: any) => (dateRefs.current[date.date] = ref)}
                isSelected={date.date === selectedSlots.dateSlotId}
                title={date.dateSlotTitle}
                description={date.dateSlotSubtitle}
                onSelected={() => handleDateSelected(date)}
                isAvailable={date.isAvailable}
                isUrgent={date.isUrgentDay}
              />
            ))}
          </Col>
          <Col className={styles.timeslotsWrapper}>
            <Row>
              <Col
                className={cx(
                  'overflow-auto',
                  // 'd-flex flex-column flex-nowrap overflow-auto padding-end-6 padding-start-2',
                  styles.timeslotsWrapper
                )}
              >
                {DateTimeSlots &&
                  DateTimeSlots.timeslots.map((slot) => (
                    <SlotBox
                      variant="slot"
                      key={slot.slotId}
                      // eslint-disable-next-line no-return-assign
                      boxRef={(ref: any) => (slotRefs.current[slot.slotId] = ref)}
                      isSelected={slot.slotId === SelectedSlot?.slotId}
                      title={capitalizeText(slot.slotText)}
                      description={capitalizeText(slot.betweenText)}
                      isAvailable={slot.isAvailable}
                      onSelected={() => handleSlotSelected(slot)}
                      isPremiumTimeSlot={slot.isPremiumTimeSlot}
                    />
                  ))}
              </Col>
            </Row>
          </Col>
        </Row>
      )}
      <StyledDiv>
        {theme.components.pages.pickupDropoff?.bottomSheet?.isShowBackButton && (
          <StyledBackButton buttonName={BUTTON_NAMES.GO_BACK} onClick={onClose}>
            <BackArrow />
          </StyledBackButton>
        )}

        <Button
          buttonName={BUTTON_NAMES.DONE}
          textColor={theme.components.pages.pickupDropoff?.button?.textColor}
          backgroundColor={theme.components.pages.pickupDropoff?.button?.background}
          block
          size="lg"
          onClick={onSubmitSheet}
          isLoading={isLoading}
        >
          {t('common.done')}
        </Button>
      </StyledDiv>
    </BottomSheet>
  );
};

export default TimeSlotSelectorComponent;
